'use client'
import { PaymentType, ToDateTime } from '@repo/shared/components'
import { type MessagesKeys, useTranslate } from '@repo/shared/hooks'
import {
  AgentFiatTransaction,
  useCancelAgentWithdrawalFiat,
} from '@repo/services'
import {
  Badge,
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
  Row,
  Typography,
} from '@repo/ui/components'
import { statusColorMapper, TStatus } from './balance.item'
import { useState } from 'react'
import Image from 'next/image'

interface BalanceDetailsDialogProps extends AgentFiatTransaction {
  children: React.ReactNode
}

export const BalanceDetailsDialog = (props: BalanceDetailsDialogProps) => {
  const {
    children,
    deposit,
    amount,
    effectiveAmount,
    status,
    createdAt,
    withdrawal,
    id,
  } = props
  const { t } = useTranslate()

  const [open, setOpen] = useState(false)
  const [showCancelWithdraw, setShowCancelWithdraw] = useState(false)
  const cancelAgentWithdrawalFiatMutation = useCancelAgentWithdrawalFiat({
    invalidate: true,
  })

  const title = deposit ? t('walletPage_deposit') : t('walletPage_withdraw')

  const handleCancelWithdraw = () => {
    if (showCancelWithdraw) {
      cancelAgentWithdrawalFiatMutation.mutate(
        {
          transactionID: id,
        },
        {
          onSuccess: () => {
            setShowCancelWithdraw(false)
            setOpen(false)
          },
        },
      )
    } else {
      setShowCancelWithdraw(true)
    }
  }

  const handleBack = () => {
    if (showCancelWithdraw) {
      setShowCancelWithdraw(false)
    } else {
      setOpen(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalTrigger asChild>{children}</ModalTrigger>
      <ModalContent className='sm:max-w-[425px]'>
        <ModalHeader>
          <ModalTitle>{!showCancelWithdraw && title}</ModalTitle>
        </ModalHeader>

        {!showCancelWithdraw && (
          <>
            <Row className='justify-between'>
              <Typography className='flex-1'>{t('common_status')}</Typography>
              <Badge
                variant={status === 'pending' ? 'solid' : 'liquid'}
                color={statusColorMapper[status as TStatus]}
              >
                {t(`walletPage_${status}` as MessagesKeys)}
              </Badge>
            </Row>
            <Row className='justify-between'>
              <Typography className='flex-1'>{t('common_time')}</Typography>
              <Typography className='text-neutral-surface-80' variant='bdi'>
                <ToDateTime date={new Date(createdAt ?? '')} />
              </Typography>
            </Row>
            <Row className='justify-between'>
              <Typography className='flex-1'>
                {t('common_total_amount')}
              </Typography>
              <PaymentType value={amount} />
            </Row>

            <Row className='justify-between'>
              <Typography className='flex-1'>{t('common_bank_fee')}</Typography>
              <PaymentType value={(amount ?? 0) - (effectiveAmount ?? 0)} />
            </Row>

            <Row className='justify-between'>
              <Typography className='flex-1'>
                {t(
                  deposit ? 'common_final_deposit' : 'common_final_withdrawal',
                )}
              </Typography>
              <PaymentType value={effectiveAmount} />
            </Row>

            {deposit?.type && (
              <Row className='justify-between'>
                <Typography className='flex-1'>{t('common_origin')}</Typography>

                <Typography weight='bold'>
                  {t(`depositType_${deposit.type}` as MessagesKeys)}
                </Typography>
              </Row>
            )}
            {withdrawal?.iban && (
              <Row className='justify-between'>
                <Typography className='flex-1'>
                  {t('common_destination')}
                </Typography>
                <Typography weight='bold' variant='bdi'>
                  IR {withdrawal?.iban?.replace('IR', '')}
                </Typography>
              </Row>
            )}
            {withdrawal?.reference && (
              <Row className='justify-between'>
                <Typography className='flex-1'>
                  {t('common_transaction_number')}
                </Typography>
                <Typography weight='bold' variant='bdi'>
                  {withdrawal?.reference}
                </Typography>
              </Row>
            )}
          </>
        )}
        {showCancelWithdraw && (
          <>
            <Image
              src='/images/danger.png'
              alt='cancel-withdraw'
              width={64}
              height={64}
              className='mx-auto'
            />
            <Typography className='text-center' size='md' weight='regular'>
              {t('walletPage_cancel_withdraw_confirm_description')}
            </Typography>
          </>
        )}
        <ModalFooter className='flex flex-col gap-md'>
          {status === 'pending' && (
            <Button
              color='negative'
              variant={showCancelWithdraw ? 'fill' : 'ghost'}
              size='md'
              width='full'
              loading={cancelAgentWithdrawalFiatMutation.isPending}
              onClick={handleCancelWithdraw}
            >
              {t(`walletPage_cancel_withdraw_confirm`)}
            </Button>
          )}
          <Button
            variant='ghost'
            width='full'
            size='md'
            color='neutral'
            onClick={handleBack}
          >
            {t('common_back')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
